import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronUp,
  Phone,
  MessageSquare,
  Image as ImageIcon,
  Award,
  Users,
  ShieldCheck,
  Clock,
  Mail,
  User,
  CheckCircle2,
  Camera,
} from "lucide-react";
import PageLayout from "@/components/PageLayout";
import Footer from "@/components/Footer";
import FloatingContactBar from "@/components/FloatingContactBar";

const CLOUD_NAME = "dizdlq9fo";
const TAG_NAME = "ba_tents_gallery";

const trustItems = [
  { icon: Award, title: "30+", sub: "Years Experience" },
  { icon: Users, title: "8000+", sub: "Projects Delivered" },
  { icon: ShieldCheck, title: "UAE-Wide", sub: "Fast Installation" },
  { icon: Clock, title: "Expert", sub: "Team & Support" },
];

const MiniQuoteForm = ({ quoteRef }: { quoteRef: React.RefObject<HTMLDivElement> }) => {
  const navigate = useNavigate();
  const [geo, setGeo] = useState({ code: "+971", country: "AE" });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    requirements: "",
  });

  useEffect(() => {
    const controller = new AbortController();

    const geoTimeout = setTimeout(() => {
      fetch("https://ipapi.co/json/", { signal: controller.signal })
        .then((res) => res.json())
        .then((data) => {
          if (data.country_calling_code) {
            const formattedCode = data.country_calling_code.startsWith("+")
              ? data.country_calling_code
              : `+${data.country_calling_code}`;

            setGeo({
              code: formattedCode,
              country: data.country_code || "AE",
            });
          }
        })
        .catch(() => {});
    }, 3000);

    return () => {
      controller.abort();
      clearTimeout(geoTimeout);
    };
  }, []);

  const handleMiniSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    navigate("/thankyou");

    const SCRIPT_URL =
      "https://script.google.com/macros/s/AKfycby5HKEZNMObZRaeTidKlOMCLR_2YpgZuP2jOscP-OHWz2pi8PT0j8giTglCwm0VO5_J/exec";

    fetch(SCRIPT_URL, {
      method: "POST",
      mode: "no-cors",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...formData,
        phone: `${geo.code} ${formData.phone}`,
        source: "Gallery Page",
      }),
    }).catch(() => {});
  };

  return (
    <div
      ref={quoteRef}
      className="relative w-full rounded-2xl bg-[#0c0c0c] shadow-none md:shadow-[0_30px_80px_rgba(0,0,0,0.55)] border-0 md:border md:border-purple-500/20 p-6 md:p-8 overflow-hidden scroll-mt-24"
    >
      <div className="hidden md:block absolute top-0 right-0 w-72 h-72 bg-purple-600/10 blur-[100px] rounded-full pointer-events-none" />

      <div className="relative z-10">
        <div className="border-b border-white/10 pb-5 mb-5">
          <h2 className="text-xl font-black text-white uppercase tracking-tight">
            Quick Quote
          </h2>
          <p className="text-[9px] font-black text-[#bd6aff] uppercase tracking-[0.24em] mt-2">
            Immediate 15-Minute Response
          </p>
        </div>

        <form className="space-y-4" onSubmit={handleMiniSubmit}>
          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              type="text"
              placeholder="Full Name"
              required
              className="w-full bg-[#0d0d0d] border border-zinc-800 rounded-xl pl-11 pr-4 py-3.5 text-zinc-200 text-[11px] focus:outline-none focus:border-purple-600 transition-colors placeholder:text-zinc-500"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>

          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              type="email"
              placeholder="Email Address"
              required
              className="w-full bg-[#0d0d0d] border border-zinc-800 rounded-xl pl-11 pr-4 py-3.5 text-zinc-200 text-[11px] focus:outline-none focus:border-purple-600 transition-colors placeholder:text-zinc-500"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
          </div>

          <div className="relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-purple-400 text-[10px] font-black border-r border-zinc-800 pr-3">
              {geo.code}
            </div>
            <input
              type="tel"
              required
              placeholder="Mobile Number"
              className="w-full bg-[#0d0d0d] border border-zinc-800 rounded-xl pl-16 pr-4 py-3.5 text-zinc-200 text-[11px] focus:outline-none focus:border-purple-600 transition-colors placeholder:text-zinc-500"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            />
          </div>

          <textarea
            placeholder="Tell us about your project requirements..."
            className="w-full h-24 md:h-28 bg-[#0d0d0d] border border-zinc-800 rounded-xl px-4 py-3.5 text-zinc-200 text-[11px] focus:outline-none resize-none focus:border-purple-600 transition-colors placeholder:text-zinc-500"
            value={formData.requirements}
            onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
          />

          <button
            type="submit"
            className="w-full py-4 bg-gradient-to-r from-[#a43cff] to-purple-700 text-white font-black rounded-xl uppercase tracking-widest text-[11px] shadow-[0_10px_30px_rgba(139,49,255,0.24)] active:scale-95 transition-all hover:brightness-110"
          >
            Send Request
          </button>
        </form>
      </div>
    </div>
  );
};

const GalleryPage = () => {
  const [images, setImages] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const quoteRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.title = "Project Gallery | BA Tents UAE";

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        "Explore BA Tents project gallery featuring event tents, pavilions, sports structures, exhibitions, temporary installations, and premium tent projects across UAE and GCC."
      );
    }

    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    window.scrollTo(0, 0);

    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 500);
    };

    const fetchImages = async () => {
      try {
        const response = await fetch(
          `https://res.cloudinary.com/${CLOUD_NAME}/image/list/${TAG_NAME}.json`
        );

        const data = await response.json();

        const urls = data.resources.map((res: any) => {
          return `https://res.cloudinary.com/${CLOUD_NAME}/image/upload/q_auto:good,f_auto,w_1200/v${res.version}/${res.public_id}.${res.format}`;
        });

        setImages(urls);
      } catch (error) {
        console.error("Error loading gallery images:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToQuote = useCallback(() => {
    quoteRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  if (loading) {
    return (
      <PageLayout>
        <div className="min-h-screen bg-black flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 rounded-full border border-purple-500/30 border-t-[#bd6aff] animate-spin" />
            <div className="text-zinc-500 font-black tracking-[0.45em] animate-pulse text-xs uppercase">
              Loading Gallery
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="min-h-screen bg-black text-white overflow-hidden w-screen max-w-none relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] -mt-28 md:-mt-12">
        <section className="relative pt-28 md:pt-32 pb-10 md:pb-16 bg-black">
          <div className="absolute inset-0 bg-black pointer-events-none" />
          <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/40 to-black pointer-events-none" />

          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid lg:grid-cols-[1.35fr_0.85fr] gap-8 lg:gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/20 bg-white/10 mb-5">
                  <ImageIcon className="w-3.5 h-3.5 text-[#bd6aff]" />
                  <span className="text-[10px] font-bold uppercase tracking-widest text-white">
                    BA Tents Project Gallery
                  </span>
                </div>

                <h1 className="text-[36px] md:text-[64px] font-black text-white leading-[1.05] mb-5 tracking-tight uppercase">
                  Project Archive <br className="hidden md:block" />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#bd6aff] to-purple-400">
                    & Gallery Showcase
                  </span>
                </h1>

                <p className="text-zinc-300 text-sm md:text-base leading-relaxed max-w-2xl mb-7">
                  Explore BA Tents event structures, sports venues, exhibition pavilions, sales centres, warehouse solutions, and premium temporary installations across UAE and GCC.
                </p>

                <div className="flex flex-row items-center gap-1.5 md:gap-3 max-w-xl mb-8">
                  <button
                    onClick={scrollToQuote}
                    className="flex-1 py-4 bg-gradient-to-r from-[#8b31ff] to-[#bd6aff] rounded-xl text-white font-black text-[10px] md:text-xs tracking-wider flex items-center justify-center gap-1.5 uppercase shadow-none md:shadow-[0_0_20px_rgba(139,49,255,0.3)] border-0 active:scale-95 transition-transform"
                  >
                    <Phone className="w-3.5 h-3.5" />
                    <span>Get Quote</span>
                  </button>

                  <a
                    href="tel:+971588243660"
                    aria-label="Call BA Tents"
                    className="flex-1 py-4 bg-white rounded-xl text-black font-black text-[10px] md:text-xs flex items-center justify-center gap-1.5 uppercase border-0 shadow-none active:scale-95 transition-transform"
                  >
                    <Phone className="w-3.5 h-3.5" />
                    <span>Call Now</span>
                  </a>

                  <a
                    href="https://wa.me/971588243660"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Contact BA Tents on WhatsApp"
                    className="flex-1 py-4 bg-emerald-500 rounded-xl text-white font-black text-[10px] md:text-xs flex items-center justify-center gap-1.5 uppercase border-0 shadow-none active:scale-95 transition-transform"
                  >
                    <MessageSquare className="w-3.5 h-3.5" />
                    <span>WhatsApp</span>
                  </a>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl pt-5 border-t border-white/10">
                  {trustItems.map((item, i) => (
                    <div key={i} className="flex items-center gap-3 text-left">
                      <div className="p-2 bg-purple-500/10 rounded-lg shrink-0">
                        <item.icon className="w-5 h-5 text-[#bd6aff]" />
                      </div>
                      <div>
                        <p className="text-white font-bold text-sm leading-none whitespace-nowrap">
                          {item.title}
                        </p>
                        <p className="text-zinc-300 text-[9px] uppercase tracking-wider font-bold mt-1.5">
                          {item.sub}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <MiniQuoteForm quoteRef={quoteRef} />
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-14">
          <div className="max-w-4xl mx-auto text-center mb-8 md:mb-12">
            <p className="text-[#bd6aff] text-[10px] font-black uppercase tracking-[0.32em] mb-3">
              Project Gallery
            </p>
            <h2 className="text-3xl md:text-6xl font-black uppercase tracking-tight text-white leading-[0.95] mb-5">
              Built Across <span className="text-[#bd6aff]">UAE & GCC</span>
            </h2>
            <p className="text-zinc-400 text-sm md:text-base leading-relaxed max-w-3xl mx-auto">
              A visual archive of our completed event, exhibition, sports, industrial, and hospitality tent installations.
            </p>
          </div>

          {images.length === 0 ? (
            <div className="min-h-[300px] rounded-2xl bg-[#080808] flex items-center justify-center text-center px-6">
              <div>
                <Camera className="w-10 h-10 text-[#bd6aff] mx-auto mb-4" />
                <p className="text-white font-black uppercase tracking-widest text-sm">
                  No Gallery Images Found
                </p>
                <p className="text-zinc-500 text-xs mt-2">
                  Please check the Cloudinary gallery tag configuration.
                </p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5 md:gap-2">
              {images.map((src, index) => (
                <motion.div
                  key={src}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: Math.min(index * 0.015, 0.18) }}
                  viewport={{ once: true, margin: "-80px" }}
                  className="relative overflow-hidden rounded-md md:rounded-lg bg-[#080808] shadow-[0_10px_30px_rgba(0,0,0,0.35)] aspect-[4/3]"
                >
                  <img
                    src={src}
                    alt={`BA Tents project gallery item ${index + 1}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                </motion.div>
              ))}
            </div>
          )}
        </section>
      </div>

      <AnimatePresence>
        {showBackToTop && (
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            onClick={scrollToTop}
            aria-label="Back to top"
            className="hidden md:flex fixed bottom-24 right-8 z-[100] group flex-col items-center gap-3"
          >
            <div className="relative w-[2px] h-16 overflow-hidden bg-white/10 rounded-full">
              <motion.div
                animate={{ y: [64, -64], opacity: [0, 1, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                className="absolute inset-0 w-full h-1/2 bg-gradient-to-t from-transparent via-white to-transparent"
              />
            </div>

            <div className="w-12 h-12 rounded-full border border-white/10 bg-white/5 backdrop-blur-sm flex items-center justify-center group-hover:bg-white group-hover:scale-110 transition-all duration-500 shadow-2xl">
              <ChevronUp className="w-6 h-6 text-white group-hover:text-black transition-colors duration-300 stroke-[3px]" />
            </div>
          </motion.button>
        )}
      </AnimatePresence>

      <FloatingContactBar onQuoteClick={scrollToQuote} />
      <Footer />
    </PageLayout>
  );
};

export default GalleryPage;
